import React, {useState, useEffect, useRef, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import EditContactForm from '../../components/modules/EditContactForm';

import {BUTTON_TYPES} from '../../utils/Constants';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {
  DLog,
  isEmpty,
  transformPhoneForAPI,
  trimSpace,
  validateEmail,
} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {CONTACTS, HTTP_METHODS} from '../../utils/URLConstants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import CommonStyles from '../../utils/CommonStyles';
import {tID} from '../../utils/TestIdHelper';

const EditContact = ({navigation, route, setOpenNewContact}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const isEdit =
    route && route.params && route.params.contactData ? true : false;
  const editData = isEdit ? route.params.contactData : null;

  const [firstName, setFirstName] = useState(
    editData ? editData.firstname : '',
  );
  const [lastName, setLastName] = useState(editData ? editData.lastname : '');
  const [email, setEmail] = useState(editData ? editData.email : '');
  const [phoneNo, setPhoneNo] = useState(
    editData ? editData.phone.replace(/-/gi, '') : '',
  );
  const [relationship, setRelationship] = useState(
    editData ? editData.relationship : '',
  );
  const [isValid, setIsValid] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validation intentionally keyed to form fields only
  }, [firstName, lastName, phoneNo, email]);

  const validateForm = () => {
    let valid = false;
    if (isEmpty(firstName)) {
      valid = false;
    } else if (isEmpty(lastName)) {
      valid = false;
    } else if (isEmpty(phoneNo) || phoneNo.length != 10) {
      valid = false;
    } else if (isEmpty(email) || !validateEmail(trimSpace(email))) {
      valid = false;
    } else {
      valid = true;
    }

    setIsValid(valid);
  };

  const onPressCancel = () => {
    if (Platform.OS !== 'web') {
      navigation.goBack();
    } else {
      setOpenNewContact(false);
    }
  };

  const onPressSave = () => {
    let method = HTTP_METHODS.POST;
    const params = {
      firstname: trimSpace(firstName),
      lastname: trimSpace(lastName),
      email: trimSpace(email),
      phone: transformPhoneForAPI(phoneNo),
      relationship: relationship,
    };

    if (isEdit) {
      params['id'] = parseInt(editData.id);
      method = HTTP_METHODS.PUT;
    }

    lpc.setVisible(true);
    fetchApi(method, CONTACTS, params)
      .then(response => {
        lpc.setVisible(false);
        if (response.status == 200) {
          if (Platform.OS !== 'web') {
            navigation.goBack();
          } else {
            setOpenNewContact(false);
          }
        }
      })
      .catch(error => {
        DLog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  return (
    <SafeAreaView
      testID={tID('EditContact-SafeAreaView-Main')}
      style={[styles.container]}>
      {Platform.OS !== 'web' ? (
        <View
          testID={tID('EditContact-View-HeaderContainer')}
          style={styles.headerView}>
          <Header
            title={isEdit ? 'Edit Contact' : 'Add Contact'}
            onPressLeftIcon={() => navigation.goBack()}
          />
        </View>
      ) : null}
      <KeyboardAvoidingView
        testID={tID('EditContact-KeyboardAvoidingView-1')}
        style={styles.contentContainer}
        behavior={Platform.OS == 'ios' ? 'padding' : null}>
        <ScrollView keyboardShouldPersistTaps="handled"
          testID={tID('EditContact-ScrollView-1')}
          style={styles.contentContainer}>
          <View style={styles.typeContainer}>
            <EditContactForm
              firstName={firstName}
              lastName={lastName}
              phoneNo={phoneNo}
              email={email}
              setFirstName={setFirstName}
              setLastName={setLastName}
              setPhoneNo={setPhoneNo}
              setEmail={setEmail}
              setRelationship={setRelationship}
              relationship={relationship}
            />
          </View>
          <View style={styles.buttonsContainer}>
            <Button
              testID={tID('EditContact-Button-Cancel')}
              style={styles.leftButton}
              onPress={onPressCancel}
              title={'Cancel'}
            />
            <Button
              testID={tID('EditContact-Button-Save')}
              disabled={!isValid}
              style={styles.rightButton}
              onPress={onPressSave}
              title={'Save'}
              buttonType={BUTTON_TYPES.Filled}
              titleColor={colors.textOnAccent}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default EditContact;
